import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $notificationStore, $transferRequestsApprovedListStore, $transferRequestsGridListStore } from '@lib/store';
import { useListState, useRouteHook } from '@shared/hooks';
import { cn, DateUtils, FormatUtils, PaletteUtils, RoutePaths } from '@shared/utils';
import { TransferRequestStatusEnum, type TransferRequestResponse } from '@shared/api';
import { ConfirmModal, OTPModal, TransferRequestConfirmModal, View } from '@widgets';
import { Icon, Pagination, Text } from '@shared/widgets';
import ShareIcon from '@assets/icons/share3.svg';
import ConfirmIcon from '@assets/icons/confirm.svg';

interface ApprovePaymentsProps {
  className?: string;
}

export const ApprovePayments = (props: ApprovePaymentsProps) => {
  const { navigate } = useRouteHook();

  const transferStore = useUnit($transferRequestsApprovedListStore.store);

  const [showApprovalModal, setShowApprovalModal] = useState<TransferRequestResponse | null>(null);
  const [showOtpModal, setShowOtpModal] = useState<TransferRequestResponse | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<TransferRequestResponse | null>(null);
  const listState = useListState({ count: 5 });

  const { t } = useTranslation('payments');

  useEffect(() => {
    return () => $transferRequestsApprovedListStore.events.clear();
  }, []);

  useEffect(() => {
    $transferRequestsApprovedListStore.events.getTransfers({
      status: TransferRequestStatusEnum.WaitingForApproval,
      FromIndex: listState.fromIndex,
      Count: listState.count,
    });
  }, [listState]);

  const submitHandler = () =>
    Promise.allSettled([
      $transferRequestsGridListStore.events.getTransfers({ Count: 10, FromIndex: 0 }),
      $transferRequestsApprovedListStore.events.getTransfers({ status: TransferRequestStatusEnum.WaitingForApproval, Count: 5, FromIndex: 0 }),
    ]);

  return (
    <>
      <section
        className={cn(
          'flex flex-col gap-3 p-6',
          PaletteUtils.isInsigniaPalette && 'bg-white dark:bg-soft-black',
          PaletteUtils.isVcfcPalette && 'bg-ghost-white dark:bg-eerie-black',
          props.className,
        )}
      >
        <Text weight={PaletteUtils.isInsigniaPalette ? 'semibold' : undefined} size={PaletteUtils.isInsigniaPalette ? '2xl' : 'xl'}>
          {t('approvePayments.title')}
        </Text>
        <View
          loading={transferStore.loading}
          noData={!transferStore.data.data.length}
          error={transferStore.error}
          onRefresh={() =>
            $transferRequestsApprovedListStore.events.getTransfers({
              status: TransferRequestStatusEnum.WaitingForApproval,
              FromIndex: listState.fromIndex,
              Count: listState.count,
            })
          }
        >
          <div className='mb-auto flex flex-col gap-2'>
            {transferStore.data.data.map((transfer) => (
              <div
                key={transfer.id}
                className={cn(
                  'flex cursor-pointer items-start justify-start gap-3 bg-white p-3 transition-shadow duration-200 dark:bg-light/5',
                  'hover:shadow-lg dark:hover:shadow-lg-dark',
                  PaletteUtils.isInsigniaPalette && 'bg-ice',
                )}
                onClick={() => setShowApprovalModal(transfer)}
              >
                <Icon
                  className={cn(
                    'flex h-9 w-9 flex-none items-center justify-center rounded-3xl dark:bg-primary',
                    PaletteUtils.isInsigniaPalette && 'bg-soft-black',
                    PaletteUtils.isVcfcPalette && 'bg-primary',
                  )}
                  color='white'
                  icon={<ShareIcon />}
                />
                {PaletteUtils.isInsigniaPalette && (
                  <ul className='flex flex-col'>
                    <Text tag='li'>
                      {transfer?.beneficiaryAmount} {transfer?.beneficiaryCurrency?.toLocaleString()}
                    </Text>
                    <Text tag='li' size='xs' color='gray'>
                      {transfer.recipient?.name || transfer.beneficiaryAccount?.name}
                    </Text>
                  </ul>
                )}
                {PaletteUtils.isVcfcPalette && (
                  <div className='flex flex-col gap-1'>
                    <ul className='flex items-center gap-1.5'>
                      <Text tag='li' weight='semibold'>
                        {transfer?.beneficiaryAmount?.toLocaleString()} {transfer?.beneficiaryCurrency}
                      </Text>
                      <Text tag='li' weight='semibold' color='gray'>
                        {transfer.recipient?.name || transfer.beneficiaryAccount?.name}
                      </Text>
                    </ul>
                    <ul className='flex gap-1'>
                      <Text tag='li' weight='semibold' size='xxs'>
                        {DateUtils.formatDate(transfer.createdOn)}
                      </Text>
                      <Text tag='li' weight='semibold' size='xxs' color='gray'>
                        {FormatUtils.transactionTypeTransformation(t(`transferRequestType.${transfer.type}`, { ns: 'common' }))}
                      </Text>
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </View>
        {!!transferStore.data.total && (
          <Pagination
            noSelect
            totalItemCount={transferStore.data.total}
            currentPage={listState.page}
            countItemPage={listState.count}
            onChangePage={listState.onChangePage}
            onChangeCountItemPage={listState.onChangeCount}
          />
        )}
      </section>
      {showApprovalModal && (
        <TransferRequestConfirmModal
          isOpen={!!showApprovalModal}
          onClose={() => setShowApprovalModal(null)}
          onOpenOtpModal={(data) => (setShowOtpModal(data), setShowApprovalModal(null))}
          onCloseOtpModal={() => setShowOtpModal(null)}
          transactionId={showApprovalModal.id}
          transferData={showApprovalModal}
          noDocumentUpload
          onReject={() => ($notificationStore.events.setWarning(t('notifications.rejected')), submitHandler())}
        />
      )}
      <OTPModal
        isOpen={!!showOtpModal}
        onClose={() => setShowOtpModal(null)}
        data={showOtpModal!}
        onSubmit={(data) => (setShowConfirmModal(data!), submitHandler())}
      />
      <ConfirmModal
        isOpen={!!showConfirmModal}
        onClose={() => setShowConfirmModal(null)}
        icon={<ConfirmIcon />}
        title={(showConfirmModal?.totalAmount || 0) >= 50000 ? t('notifications.approved.title2') : t('notifications.approved.title')}
        description={
          <div className='mb-4 flex flex-col gap-2 text-center'>
            <p>{t('notifications.approved.subtitle')}</p>
            {(showConfirmModal?.totalAmount || 0) >= 50000 && <p>{t('notifications.approved.description')}</p>}
          </div>
        }
        buttonTitle='Transaction page'
        onClick={() =>
          showConfirmModal?.paymentId &&
          navigate(RoutePaths.accountTransaction.onNavigate(showConfirmModal.paymentId, { back: RoutePaths.payments.onNavigate() }))
        }
      />
    </>
  );
};
